import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../lib/firebase';
import { supabase } from '../../lib/supabaseClient';
import imageCompression from 'browser-image-compression';
import {
  Video, Send, Image as ImageIcon, X, Trash2, Edit2, Heart, MessageCircle,
  User as UserIcon, Store, ArrowLeft, Users, Maximize2, Minimize2, Move, Mic,
  ChevronLeft, ChevronRight, Expand, Shrink, Home, Plus, Bookmark, Eye,
  Share2, Music, Search, Check, Link as LinkIcon, Play, PhoneOff
} from 'lucide-react';
import './OpenMarket.css';
import VerifiedBadge from '../verified/VerifiedBadge';

// ============================================================
// Types
// ============================================================
interface OpenMarketSettings {
  id: number;
  active_status: boolean;
  open_date: string | null;
  close_date: string | null;
  schedule_type: 'once' | 'regular';
}

interface VideoRoom {
  id: string;
  room_name: string;
  created_by_user_id: string;
  created_by_name: string;
  created_by_avatar?: string;
  participant_count: number;
  is_active: boolean;
  created_at: string;
  last_activity: string;
  live_link?: string | null;
}

interface VendorShop {
  id: string;
  vendor_id: string;
  shop_name: string;
  profile_image: string;
}

interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  user_name: string;
  user_avatar?: string;
  text: string;
  created_at: string;
}

interface Viewer {
  id: string;
  post_id: string;
  user_id: string | null;
  poster_id: string;
  viewer_type: 'user' | 'visitor';
  views: number;
  last_viewed_at: string;
  viewer_name?: string;
  viewer_avatar?: string;
}

interface Post {
  id: string;
  user_id: string;
  text: string;
  media_urls: string[] | null;
  media_types: string[] | null;
  created_at: string;
  expires_at: string;
  updated_at: string;
  user_name?: string;
  user_avatar?: string;
  vendor_shops?: VendorShop[];
  comments?: Comment[];
  comments_count?: number;
  likes_count?: number;
  is_liked_by_user?: boolean;
  live_room_id?: string | null;
  views_count?: number;
  _score?: number;
}

type TabKey = 'new' | 'trending' | 'foryou' | 'following' | 'live' | 'insight';
type NavFilter = 'none' | 'liked' | 'me';

// ============================================================
// Helpers
// ============================================================
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const compressImage = async (file: File): Promise<File> => {
  const options = { maxSizeMB: 3, maxWidthOrHeight: 1920, useWebWorker: true };
  try {
    return await imageCompression(file, options);
  } catch {
    return file;
  }
};

const POST_LIFETIME_DAYS = 90;
const MAX_FILE_SIZE_MB = 30;
const PAGE_SIZE = 10;
const PAGE_SIZE_MORE = 4;
const MAX_HASHTAGS = 5;
const VIEW_THRESHOLD_MS = 3000;
const TAB_STORAGE_KEY = 'social_active_tab';

// Mirotalk base — same as old version. Both host and joiner derive the
// iframe URL from the immutable room_name, so no link sharing is needed.
const MIROTALK_BASE = 'https://p2p.mirotalk.com/join?room=';

const buildPostText = (title: string, description: string, hashtags: string[]): string => {
  const cleanTitle = title.trim();
  const cleanDesc = description.trim();
  const cleanTags = hashtags
    .map(t => t.trim())
    .filter(Boolean)
    .map(t => (t.startsWith('#') ? t : `#${t}`));

  const parts: string[] = [];
  if (cleanTitle) parts.push(cleanTitle);
  if (cleanDesc) parts.push(cleanDesc);
  const tagBlock = cleanTags.join('');
  if (tagBlock) parts.push(tagBlock);
  return parts.join('_');
};

const parsePostText = (raw: string): { title: string; description: string; hashtags: string[] } => {
  if (!raw) return { title: '', description: '', hashtags: [] };
  const tagMatches = raw.match(/#[^\s#_]+/g) || [];
  let withoutTags = raw;
  if (tagMatches.length) {
    withoutTags = raw.replace(/(#[^\s#_]+)+$/g, '').replace(/_+$/, '');
  }
  const segments = withoutTags.split('_');
  if (segments.length <= 1) {
    return { title: segments[0] || '', description: '', hashtags: tagMatches };
  }
  return {
    title: segments[0],
    description: segments.slice(1).join('_'),
    hashtags: tagMatches
  };
};

const formatTimeAgo = (dateStr: string) => {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const getDeviceId = (): string => {
  try {
    let did = localStorage.getItem('social_device_id');
    if (!did) {
      did = `visitor_${Math.random().toString(36).slice(2)}_${Date.now()}`;
      localStorage.setItem('social_device_id', did);
    }
    return did;
  } catch {
    return `visitor_temp_${Date.now()}`;
  }
};

const getSavedTab = (): TabKey => {
  try {
    const t = localStorage.getItem(TAB_STORAGE_KEY);
    if (t === 'new' || t === 'trending' || t === 'foryou' || t === 'following' || t === 'live' || t === 'insight') {
      return t as TabKey;
    }
  } catch { /* noop */ }
  return 'new';
};

// ============================================================
// Component
// ============================================================
const OpenMarket: React.FC = () => {
  const navigate = useNavigate();

  // ---------- auth ----------
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userName, setUserName] = useState('');
  const [userAvatar, setUserAvatar] = useState('');
  const [userShops, setUserShops] = useState<VendorShop[]>([]);
  const [settings, setSettings] = useState<OpenMarketSettings | null>(null);
  const [isMarketOpen, setIsMarketOpen] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState('');

  // ---------- feed ----------
  const [tab, setTab] = useState<TabKey>(getSavedTab());
  const [navFilter, setNavFilter] = useState<NavFilter>('none');
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageOffset, setPageOffset] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  // ---------- follow cache ----------
  const [followedVendorOwnerIds, setFollowedVendorOwnerIds] = useState<Set<string>>(new Set());

  // ---------- live ----------
  const [videoRooms, setVideoRooms] = useState<VideoRoom[]>([]);
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [activeLiveRoom, setActiveLiveRoom] = useState<VideoRoom | null>(null);
  const [liveVideoMinimized, setLiveVideoMinimized] = useState(false);
  const [liveVideoSize, setLiveVideoSize] = useState({ width: 320, height: 240 });
  const [liveVideoPosition, setLiveVideoPosition] = useState({ x: 20, y: 80 });
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; initialWidth: number; initialHeight: number } | null>(null);
  const liveVideoRef = useRef<HTMLDivElement>(null);

  // ---------- composer (multi-step) ----------
  const [showComposer, setShowComposer] = useState(false);
  const [composerStep, setComposerStep] = useState<1 | 2 | 3 | 4>(1);
  const [newPostMedia, setNewPostMedia] = useState<{ file: File; preview: string; type: 'image' | 'video' | 'audio' }[]>([]);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostDescription, setNewPostDescription] = useState('');
  const [newPostHashtags, setNewPostHashtags] = useState<string[]>(['', '', '', '', '']);
  const [uploadingPost, setUploadingPost] = useState(false);
  const [recordingAudio, setRecordingAudio] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // ---------- post actions ----------
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [editingHashtags, setEditingHashtags] = useState<string[]>(['', '', '', '', '']);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);

  // ---------- comments ----------
  const [commentsOpenFor, setCommentsOpenFor] = useState<string | null>(null);
  const [commentInput, setCommentInput] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // ---------- views modal ----------
  const [viewsOpenFor, setViewsOpenFor] = useState<string | null>(null);
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [loadingViewers, setLoadingViewers] = useState(false);

  // ---------- fullscreen media ----------
  const [selectedPostMedia, setSelectedPostMedia] = useState<{ urls: string[]; types: string[]; index: number } | null>(null);

  // ---------- search ----------
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchMode, setSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<Post[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);

  // ---------- playback ----------
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const [videoPaused, setVideoPaused] = useState<Record<string, boolean>>({});
  const viewTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentViewPostRef = useRef<string | null>(null);

  // ---------- toast ----------
  const [toast, setToast] = useState<string | null>(null);
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1800);
  }, []);

  // ---------- persist tab choice ----------
  useEffect(() => {
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch { /* noop */ }
  }, [tab]);

  // ============================================================
  // Auth
  // ============================================================
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        let name = user.displayName || user.email?.split('@')[0] || 'User';
        let avatar = '';
        try {
          const { data } = await supabase
            .from('users')
            .select('name, avatar_url')
            .eq('firebase_uid', user.uid)
            .single();
          if (data?.name) name = data.name;
          if (data?.avatar_url) avatar = data.avatar_url;
        } catch { /* silent */ }
        setUserName(name);
        setUserAvatar(avatar);

        const { data: shops } = await supabase
          .from('vendor_profiles')
          .select('id, vendor_id, shop_name, profile_image')
          .eq('user_id', user.uid);
        setUserShops(shops || []);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // ============================================================
  // Followed vendors
  // ============================================================
  const loadFollowedVendors = useCallback(async () => {
    if (!currentUser) return;
    try {
      const { data: follows } = await supabase
        .from('vendor_follow')
        .select('vendor_id')
        .eq('user_id', currentUser.uid);
      if (!follows?.length) { setFollowedVendorOwnerIds(new Set()); return; }
      const vendorIds = follows.map(f => f.vendor_id);
      const { data: vendors } = await supabase
        .from('vendor_profiles')
        .select('user_id, vendor_id')
        .in('vendor_id', vendorIds);
      const ownerIds = new Set<string>();
      (vendors || []).forEach(v => v.user_id && ownerIds.add(v.user_id));
      setFollowedVendorOwnerIds(ownerIds);
    } catch { setFollowedVendorOwnerIds(new Set()); }
  }, [currentUser]);

  // ============================================================
  // Settings
  // ============================================================
  const loadSettings = useCallback(async () => {
    try {
      const { data } = await supabase.from('openmarket_settings').select('*').single();
      if (!data) return;
      setSettings(data);
      const now = new Date();
      if (!data.active_status) { setIsMarketOpen(false); setScheduleMessage('Market closed'); return; }
      if (data.open_date && data.close_date) {
        const open = new Date(data.open_date);
        const close = new Date(data.close_date);
        if (now >= open && now <= close) {
          setIsMarketOpen(true);
          setScheduleMessage(`Open until ${close.toLocaleTimeString()}`);
        } else {
          setIsMarketOpen(false);
          setScheduleMessage(now < open ? `Opens ${open.toLocaleDateString()}` : 'Market closed');
        }
      } else { setIsMarketOpen(true); setScheduleMessage('Open now'); }
    } catch { /* silent */ }
  }, []);

  // ============================================================
  // Live rooms — identical core logic to old version
  // ============================================================
  const loadVideoRooms = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('openmarket_video_rooms')
        .select('*')
        .eq('is_active', true)
        .order('participant_count', { ascending: false })
        .order('created_at', { ascending: false });

      const userIds: string[] = [];
      (data || []).forEach(r => { if (!userIds.includes(r.created_by_user_id)) userIds.push(r.created_by_user_id); });

      const avatars: Record<string, string> = {};
      if (userIds.length) {
        const { data: users } = await supabase
          .from('users')
          .select('firebase_uid, avatar_url')
          .in('firebase_uid', userIds);
        (users || []).forEach(u => { avatars[u.firebase_uid] = u.avatar_url || ''; });
      }
      setVideoRooms((data || []).map(r => ({ ...r, created_by_avatar: avatars[r.created_by_user_id] })));
    } catch { /* silent */ }
  }, []);

  const generateUniqueRoomName = useCallback(async (baseName: string): Promise<string> => {
    let roomName = baseName;
    let counter = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { data } = await supabase
        .from('openmarket_video_rooms')
        .select('id')
        .eq('room_name', roomName)
        .maybeSingle();
      if (!data) return roomName;
      counter++;
      roomName = `${baseName}${counter}`;
    }
  }, []);

  // ============================================================
  // Enrich posts
  // ============================================================
  const enrichPosts = useCallback(async (rawPosts: any[]): Promise<Post[]> => {
    if (!rawPosts.length) return [];

    const userIds: string[] = [];
    rawPosts.forEach(p => { if (!userIds.includes(p.user_id)) userIds.push(p.user_id); });

    const userProfiles: Record<string, { name: string; avatar: string }> = {};
    if (userIds.length) {
      const { data: users } = await supabase
        .from('users')
        .select('firebase_uid, name, avatar_url')
        .in('firebase_uid', userIds);
      (users || []).forEach(u => {
        userProfiles[u.firebase_uid] = { name: u.name || 'User', avatar: u.avatar_url || '' };
      });
    }

    const userShopsMap: Record<string, VendorShop[]> = {};
    if (userIds.length) {
      const { data: shops } = await supabase
        .from('vendor_profiles')
        .select('user_id, id, vendor_id, shop_name, profile_image')
        .in('user_id', userIds);
      (shops || []).forEach(shop => {
        if (!userShopsMap[shop.user_id]) userShopsMap[shop.user_id] = [];
        userShopsMap[shop.user_id].push({
          id: shop.id,
          vendor_id: shop.vendor_id,
          shop_name: shop.shop_name,
          profile_image: shop.profile_image || ''
        });
      });
    }

    const postIds = rawPosts.map(p => p.id);
    const commentsCount: Record<string, number> = {};
    if (postIds.length) {
      const { data: c } = await supabase
        .from('openmarket_comments')
        .select('post_id')
        .in('post_id', postIds);
      (c || []).forEach(row => {
        commentsCount[row.post_id] = (commentsCount[row.post_id] || 0) + 1;
      });
    }

    const likesCount: Record<string, number> = {};
    const userLikes = new Set<string>();
    if (postIds.length && currentUser) {
      const { data: likes } = await supabase
        .from('openmarket_post_likes')
        .select('post_id, user_id')
        .in('post_id', postIds);
      (likes || []).forEach(l => {
        likesCount[l.post_id] = (likesCount[l.post_id] || 0) + 1;
        if (l.user_id === currentUser.uid) userLikes.add(l.post_id);
      });
    }

    return rawPosts.map(post => {
      const likes = likesCount[post.id] || 0;
      const comments = commentsCount[post.id] || 0;
      return {
        ...post,
        media_urls: post.media_urls || null,
        media_types: post.media_types || null,
        user_name: userProfiles[post.user_id]?.name || 'User',
        user_avatar: userProfiles[post.user_id]?.avatar || '',
        vendor_shops: userShopsMap[post.user_id] || [],
        comments_count: comments,
        likes_count: likes,
        is_liked_by_user: userLikes.has(post.id),
        views_count: post.views || 0,
        _score: likes + comments
      };
    });
  }, [currentUser]);

  // ============================================================
  // Fetch page
  // ============================================================
  const fetchPostPage = useCallback(async (
    tabKey: TabKey,
    filter: NavFilter,
    offset: number,
    limit: number
  ): Promise<{ items: Post[]; exhausted: boolean }> => {
    const nowIso = new Date().toISOString();
    let query = supabase
      .from('openmarket_posts')
      .select('*')
      .gt('expires_at', nowIso);

    if (filter === 'liked' && currentUser) {
      const { data: liked } = await supabase
        .from('openmarket_post_likes')
        .select('post_id')
        .eq('user_id', currentUser.uid);
      const ids = (liked || []).map(l => l.post_id);
      if (!ids.length) return { items: [], exhausted: true };
      query = query.in('id', ids);
    } else if (filter === 'me' && currentUser) {
      query = query.eq('user_id', currentUser.uid);
    } else {
      if (tabKey === 'live') {
        query = query.not('live_room_id', 'is', null);
      } else if (tabKey === 'following') {
        const ownerIds = Array.from(followedVendorOwnerIds);
        if (!ownerIds.length) return { items: [], exhausted: true };
        query = query.in('user_id', ownerIds);
      }
    }

    if (tabKey === 'trending' && filter === 'none') {
      query = query.order('views', { ascending: false, nullsFirst: false });
    } else {
      query = query.order('created_at', { ascending: false });
    }
    query = query.range(offset, offset + limit - 1);

    const { data, error } = await query;
    if (error) return { items: [], exhausted: true };

    const raw = data || [];
    const enriched = await enrichPosts(raw);

    if (tabKey === 'foryou' && filter === 'none') {
      enriched.sort((a, b) => (b._score || 0) - (a._score || 0));
    }

    return { items: enriched, exhausted: raw.length < limit };
  }, [currentUser, followedVendorOwnerIds, enrichPosts]);

  const loadFeed = useCallback(async (reset: boolean) => {
    if (reset) { setLoading(true); setPageOffset(0); setHasMore(true); }
    else setLoadingMore(true);

    const offset = reset ? 0 : pageOffset;
    const limit = reset ? PAGE_SIZE : PAGE_SIZE_MORE;

    const { items, exhausted } = await fetchPostPage(tab, navFilter, offset, limit);

    if (reset) {
      setPosts(items);
      setActiveIndex(0);
      setPageOffset(items.length);
    } else {
      setPosts(prev => {
        const seen = new Set(prev.map(p => p.id));
        const merged = [...prev];
        items.forEach(it => { if (!seen.has(it.id)) merged.push(it); });
        return merged;
      });
      setPageOffset(prev => prev + items.length);
    }
    setHasMore(!exhausted);
    if (reset) setLoading(false); else setLoadingMore(false);
  }, [tab, navFilter, pageOffset, fetchPostPage]);

  // ============================================================
  // Comments
  // ============================================================
  const loadCommentsForPost = useCallback(async (postId: string): Promise<Comment[]> => {
    const { data } = await supabase
      .from('openmarket_comments')
      .select('*')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });
    const userIds: string[] = [];
    (data || []).forEach(c => { if (!userIds.includes(c.user_id)) userIds.push(c.user_id); });
    const avatars: Record<string, string> = {};
    if (userIds.length) {
      const { data: users } = await supabase
        .from('users')
        .select('firebase_uid, avatar_url')
        .in('firebase_uid', userIds);
      (users || []).forEach(u => { avatars[u.firebase_uid] = u.avatar_url || ''; });
    }
    return (data || []).map(c => ({ ...c, user_avatar: avatars[c.user_id] }));
  }, []);

  // ============================================================
  // Boot + polling
  // ============================================================
  useEffect(() => {
    loadSettings();
    loadVideoRooms();
    if (currentUser) loadFollowedVendors();
  }, [currentUser, loadSettings, loadVideoRooms, loadFollowedVendors]);

  useEffect(() => {
    loadFeed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, tab, navFilter, followedVendorOwnerIds]);

  useEffect(() => {
    const interval = setInterval(() => { loadVideoRooms(); }, 30000);
    return () => clearInterval(interval);
  }, [loadVideoRooms]);

  // ============================================================
  // Auto-cleanup rooms
  // ============================================================
  useEffect(() => {
    const cleanup = setInterval(async () => {
      const twoMinsAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString();
      await supabase
        .from('openmarket_video_rooms')
        .update({ is_active: false })
        .lt('last_activity', twoMinsAgo)
        .eq('is_active', true);
      loadVideoRooms();
    }, 60000);
    return () => clearInterval(cleanup);
  }, [loadVideoRooms]);

  // ============================================================
  // Video autoplay
  // ============================================================
  useEffect(() => {
    const current = posts[activeIndex];
    if (!current) return;
    Object.entries(videoRefs.current).forEach(([postId, vid]) => {
      if (!vid) return;
      if (postId !== current.id) {
        try { vid.pause(); } catch { /* noop */ }
      }
    });
    const currentEl = videoRefs.current[current.id];
    if (currentEl && !videoPaused[current.id]) {
      currentEl.play().catch(() => { /* noop */ });
    }
  }, [activeIndex, posts, videoPaused]);

  // ============================================================
  // 3-second view tracking
  // ============================================================
  const recordPostView = useCallback(async (post: Post) => {
    try {
      const userId = currentUser?.uid || null;
      const deviceId = userId ? null : getDeviceId();
      const identifier = userId || deviceId || 'anonymous';
      const viewerType: 'user' | 'visitor' = userId ? 'user' : 'visitor';

      const { data: existing } = await supabase
        .from('openmarket_post_views')
        .select('id, views')
        .eq('post_id', post.id)
        .eq('viewer_identifier', identifier)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('openmarket_post_views')
          .update({
            views: (existing.views || 0) + 1,
            last_viewed_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('openmarket_post_views')
          .insert({
            post_id: post.id,
            user_id: userId,
            poster_id: post.user_id,
            viewer_identifier: identifier,
            viewer_type: viewerType,
            views: 1,
            last_viewed_at: new Date().toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
      }

      await supabase.rpc('increment_post_views', { p_post_id: post.id });

      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, views_count: (p.views_count || 0) + 1 } : p));
    } catch { /* silent */ }
  }, [currentUser]);

  useEffect(() => {
    if (!posts[activeIndex]) return;
    const post = posts[activeIndex];

    if (currentViewPostRef.current === post.id) return;
    currentViewPostRef.current = post.id;

    if (viewTimerRef.current) clearTimeout(viewTimerRef.current);
    viewTimerRef.current = setTimeout(() => {
      recordPostView(post);
    }, VIEW_THRESHOLD_MS);

    return () => {
      if (viewTimerRef.current) clearTimeout(viewTimerRef.current);
    };
  }, [activeIndex, posts, recordPostView]);

  // ============================================================
  // Feed scroll + infinite load
  // ============================================================
  const feedRef = useRef<HTMLDivElement>(null);
  const handleFeedScroll = useCallback(() => {
    const el = feedRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / el.clientHeight);
    if (idx !== activeIndex) setActiveIndex(idx);
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - el.clientHeight * 1.2;
    if (nearBottom && hasMore && !loadingMore && !loading) loadFeed(false);
  }, [activeIndex, hasMore, loadingMore, loading, loadFeed]);

  // ============================================================
  // LIVE actions — identical core logic to old version.
  // Both host and joiner render the SAME Mirotalk iframe derived
  // from room_name. No link sharing required.
  // ============================================================
  const createLivePost = useCallback(async (roomId: string, roomName: string) => {
    if (!currentUser) return;
    await supabase.from('openmarket_posts').insert({
      user_id: currentUser.uid,
      text: `🔴 Went live: ${roomName}`,
      media_urls: null,
      media_types: null,
      live_room_id: roomId,
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + POST_LIFETIME_DAYS * 24 * 60 * 60 * 1000).toISOString()
    });
    await loadFeed(true);
  }, [currentUser, loadFeed]);

  const handleGoLive = async () => {
    if (!currentUser || creatingRoom) return;
    setCreatingRoom(true);
    try {
      const firstName = userName.split(' ')[0] || 'User';
      const roomName = await generateUniqueRoomName(firstName);
      const { data, error } = await supabase
        .from('openmarket_video_rooms')
        .insert({
          room_name: roomName,
          created_by_user_id: currentUser.uid,
          created_by_name: userName,
          participant_count: 1,
          is_active: true,
          last_activity: new Date().toISOString()
        })
        .select()
        .single();
      if (error) throw error;

      await createLivePost(data.id, roomName);

      setActiveLiveRoom({ ...data, created_by_avatar: userAvatar });
      setLiveVideoMinimized(false);
      setLiveVideoSize({ width: 320, height: 240 });
      await loadVideoRooms();
      showToast('Live started');
    } catch { /* silent */ }
    finally { setCreatingRoom(false); }
  };

  // Join a room: increment participant count, refresh rooms, open iframe.
  const handleJoinRoom = async (room: VideoRoom) => {
    try {
      await supabase
        .from('openmarket_video_rooms')
        .update({
          participant_count: (room.participant_count || 0) + 1,
          last_activity: new Date().toISOString(),
          is_active: true
        })
        .eq('id', room.id);
      // Refresh rooms so participant count updates everywhere
      await loadVideoRooms();
    } catch { /* silent */ }
    setActiveLiveRoom(room);
    setLiveVideoMinimized(false);
    setLiveVideoSize({ width: 320, height: 240 });
  };

  // End live — mark inactive, remove the live post, close panel.
  const handleEndLive = async () => {
    if (!activeLiveRoom) return;
    try {
      await supabase
        .from('openmarket_video_rooms')
        .update({ is_active: false })
        .eq('id', activeLiveRoom.id);
      await supabase
        .from('openmarket_posts')
        .delete()
        .eq('live_room_id', activeLiveRoom.id);
    } catch { /* silent */ }
    setActiveLiveRoom(null);
    await loadVideoRooms();
    await loadFeed(true);
  };

  // Leave (non-owner closes panel). Decrement participant count.
  const handleLeaveRoom = async () => {
    if (!activeLiveRoom) return;
    try {
      await supabase
        .from('openmarket_video_rooms')
        .update({
          participant_count: Math.max(0, (activeLiveRoom.participant_count || 1) - 1),
          last_activity: new Date().toISOString()
        })
        .eq('id', activeLiveRoom.id);
      await loadVideoRooms();
    } catch { /* silent */ }
    setActiveLiveRoom(null);
  };

  // Fallback — kept from new version, harmless: host can paste an
  // external link to override the Mirotalk URL if they want.
  const handlePasteLiveLink = async (roomId: string) => {
    try {
      const text = await navigator.clipboard.readText();
      const link = (text || '').trim();
      if (!link) return;

      await supabase
        .from('openmarket_video_rooms')
        .update({
          live_link: link,
          last_activity: new Date().toISOString()
        })
        .eq('id', roomId);

      setVideoRooms(prev => prev.map(r => r.id === roomId ? { ...r, live_link: link } : r));
      setActiveLiveRoom(prev => prev && prev.id === roomId ? { ...prev, live_link: link } : prev);
      showToast('Pasted');
    } catch { /* silent */ }
  };

  // drag/resize
  const handleDragStart = (e: React.MouseEvent) => {
    if (!liveVideoRef.current) return;
    e.preventDefault();
    const rect = liveVideoRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, initialX: rect.left, initialY: rect.top };
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  };
  const handleDragMove = (e: MouseEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setLiveVideoPosition({
      x: Math.max(0, Math.min(window.innerWidth - liveVideoSize.width, dragRef.current.initialX + dx)),
      y: Math.max(0, Math.min(window.innerHeight - liveVideoSize.height, dragRef.current.initialY + dy))
    });
  };
  const handleDragEnd = () => {
    dragRef.current = null;
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
  };
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      startX: e.clientX, startY: e.clientY,
      initialWidth: liveVideoSize.width, initialHeight: liveVideoSize.height
    };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };
  const handleResizeMove = (e: MouseEvent) => {
    if (!resizeRef.current) return;
    const dx = e.clientX - resizeRef.current.startX;
    const dy = e.clientY - resizeRef.current.startY;
    setLiveVideoSize({
      width: Math.max(240, Math.min(window.innerWidth - 40, resizeRef.current.initialWidth + dx)),
      height: Math.max(180, Math.min(window.innerHeight - 100, resizeRef.current.initialHeight + dy))
    });
  };
  const handleResizeEnd = () => {
    resizeRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  // ============================================================
  // Like / Save / Share
  // ============================================================
  const handleLikePost = async (postId: string) => {
    if (!currentUser) return;
    const post = posts.find(p => p.id === postId);
    if (!post) return;

    setPosts(prev => prev.map(p => p.id === postId
      ? { ...p, is_liked_by_user: !p.is_liked_by_user, likes_count: (p.likes_count || 0) + (p.is_liked_by_user ? -1 : 1) }
      : p
    ));

    if (post.is_liked_by_user) {
      await supabase.from('openmarket_post_likes').delete().eq('post_id', postId).eq('user_id', currentUser.uid);
    } else {
      await supabase.from('openmarket_post_likes').insert({ post_id: postId, user_id: currentUser.uid });
    }
  };

  const handleSavePost = async (post: Post) => {
    try {
      const firstUrl = post.media_urls?.[0];
      const firstType = post.media_types?.[0] || 'image';
      if (!firstUrl) { await handleLikePost(post.id); return; }
      const res = await fetch(firstUrl);
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const ext = firstType === 'video' ? 'mp4' : firstType === 'audio' ? 'mp3' : 'jpg';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `social-${post.id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      showToast('Saved');
    } catch {
      await handleLikePost(post.id);
    }
  };

  const handleSharePost = async (post: Post) => {
    let shareUrl = `${window.location.origin}/openmarket/post/${post.id}`;
    if (post.vendor_shops && post.vendor_shops.length > 0) {
      shareUrl = `${window.location.origin}/vendor/${post.vendor_shops[0].vendor_id}`;
    }
    const fallbackSite = 'https://Gostorez.com/';

    const shareText = post.text || 'Shared from Social';
    const shareData = {
      title: 'Check this out on Social',
      text: shareText,
      url: shareUrl
    };

    try {
      if (navigator.share) {
        try {
          await navigator.share(shareData);
          showToast('Link copied');
          return;
        } catch { /* cancelled */ }
      }
      try {
        await navigator.clipboard.writeText(shareUrl);
        showToast('Link copied');
      } catch {
        try { await navigator.clipboard.writeText(fallbackSite); showToast('Link copied'); } catch { /* silent */ }
      }
    } catch { /* silent */ }
  };

  // ============================================================
  // Comments
  // ============================================================
  const handleOpenComments = async (postId: string) => {
    setCommentsOpenFor(postId);
    setCommentInput('');
    const comments = await loadCommentsForPost(postId);
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments } : p));
  };

  const handleAddComment = async (postId: string) => {
    if (!currentUser || !commentInput.trim()) return;
    setSubmittingComment(true);
    try {
      await supabase.from('openmarket_comments').insert({
        post_id: postId,
        user_id: currentUser.uid,
        user_name: userName,
        text: commentInput.trim()
      });
      setCommentInput('');
      const comments = await loadCommentsForPost(postId);
      setPosts(prev => prev.map(p => p.id === postId
        ? { ...p, comments, comments_count: comments.length }
        : p));
    } catch { /* silent */ }
    finally { setSubmittingComment(false); }
  };

  // ============================================================
  // Views modal
  // ============================================================
  const handleOpenViews = async (postId: string) => {
    setViewsOpenFor(postId);
    setLoadingViewers(true);
    try {
      const { data } = await supabase
        .from('openmarket_post_views')
        .select('*')
        .eq('post_id', postId)
        .order('views', { ascending: false });

      const userIds: string[] = [];
      (data || []).forEach((v: any) => {
        if (v.user_id && !userIds.includes(v.user_id)) userIds.push(v.user_id);
      });

      const profiles: Record<string, { name: string; avatar: string }> = {};
      if (userIds.length) {
        const { data: users } = await supabase
          .from('users')
          .select('firebase_uid, name, avatar_url')
          .in('firebase_uid', userIds);
        (users || []).forEach(u => {
          profiles[u.firebase_uid] = { name: u.name || 'User', avatar: u.avatar_url || '' };
        });
      }

      const enriched: Viewer[] = (data || []).map((v: any) => {
        const isVisitor = v.viewer_type === 'visitor' || !v.user_id;
        return {
          id: v.id,
          post_id: v.post_id,
          user_id: v.user_id,
          poster_id: v.poster_id,
          viewer_type: isVisitor ? 'visitor' : 'user',
          views: v.views || 1,
          last_viewed_at: v.last_viewed_at || v.created_at,
          viewer_name: isVisitor ? 'Visitor' : (profiles[v.user_id]?.name || 'User'),
          viewer_avatar: isVisitor ? '' : (profiles[v.user_id]?.avatar || '')
        };
      });

      setViewers(enriched);
    } catch { setViewers([]); }
    finally { setLoadingViewers(false); }
  };

  // ============================================================
  // Composer — multi-step
  // ============================================================
  const handleOpenComposer = () => {
    setComposerStep(1);
    setNewPostMedia([]);
    setNewPostTitle('');
    setNewPostDescription('');
    setNewPostHashtags(['', '', '', '', '']);
    setShowComposer(true);
  };

  const handleCloseComposer = () => {
    setShowComposer(false);
    setComposerStep(1);
    setNewPostMedia([]);
    setNewPostTitle('');
    setNewPostDescription('');
    setNewPostHashtags(['', '', '', '', '']);
  };

  const handleMediaSelect = (files: FileList | null) => {
    if (!files) return;
    const accepted = Array.from(files);
    for (const file of accepted) {
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > MAX_FILE_SIZE_MB) continue;
      let type: 'image' | 'video' | 'audio';
      if (file.type.startsWith('image/')) type = 'image';
      else if (file.type.startsWith('video/')) type = 'video';
      else if (file.type.startsWith('audio/')) type = 'audio';
      else continue;
      const preview = URL.createObjectURL(file);
      setNewPostMedia(prev => [...prev, { file, preview, type }]);
    }
  };

  const handleRemoveMedia = (index: number) => {
    setNewPostMedia(prev => prev.filter((_, i) => i !== index));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
        const preview = URL.createObjectURL(file);
        setNewPostMedia(prev => [...prev, { file, preview, type: 'audio' }]);
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setRecordingAudio(true);
    } catch { /* silent */ }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingAudio) {
      mediaRecorderRef.current.stop();
      setRecordingAudio(false);
    }
  };

  const handleHashtagChange = (idx: number, val: string) => {
    setNewPostHashtags(prev => {
      const next = [...prev];
      next[idx] = val.replace(/^#+/, '');
      return next;
    });
  };

  const handleSubmitPost = async () => {
    if (!currentUser) return;
    const title = newPostTitle.trim();
    const description = newPostDescription.trim();
    const hashtags = newPostHashtags.map(h => h.trim()).filter(Boolean);
    const hasContent = newPostMedia.length > 0 || title || description || hashtags.length > 0;
    if (!hasContent) return;

    setUploadingPost(true);
    try {
      const mediaUrls: string[] = [];
      const mediaTypes: string[] = [];
      for (const item of newPostMedia) {
        let file = item.file;
        if (item.type === 'image') file = await compressImage(file);
        const base64 = await fileToBase64(file);
        mediaUrls.push(base64);
        mediaTypes.push(item.type);
      }

      const text = buildPostText(title, description, hashtags);

      await supabase.from('openmarket_posts').insert({
        user_id: currentUser.uid,
        text: text || null,
        media_urls: mediaUrls.length ? mediaUrls : null,
        media_types: mediaTypes.length ? mediaTypes : null,
        created_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + POST_LIFETIME_DAYS * 24 * 60 * 60 * 1000).toISOString()
      });

      handleCloseComposer();
      await loadFeed(true);
    } catch { /* silent */ }
    finally { setUploadingPost(false); }
  };

  // ============================================================
  // Edit / Delete
  // ============================================================
  const handleStartEdit = (post: Post) => {
    const parsed = parsePostText(post.text || '');
    setEditingPostId(post.id);
    setEditingTitle(parsed.title);
    setEditingDescription(parsed.description);
    const tags = [...parsed.hashtags];
    while (tags.length < MAX_HASHTAGS) tags.push('');
    setEditingHashtags(tags.slice(0, MAX_HASHTAGS).map(t => t.replace(/^#/, '')));
  };

  const handleEditHashtagChange = (idx: number, val: string) => {
    setEditingHashtags(prev => {
      const next = [...prev];
      next[idx] = val.replace(/^#+/, '');
      return next;
    });
  };

  const handleEditPost = async (postId: string) => {
    const tags = editingHashtags.map(h => h.trim()).filter(Boolean);
    const built = buildPostText(editingTitle, editingDescription, tags);
    await supabase
      .from('openmarket_posts')
      .update({ text: built || null, updated_at: new Date().toISOString() })
      .eq('id', postId)
      .eq('user_id', currentUser?.uid);
    setEditingPostId(null);
    setEditingTitle('');
    setEditingDescription('');
    setEditingHashtags(['', '', '', '', '']);
    await loadFeed(true);
  };

  const handleDeletePost = async (postId: string) => {
    setDeletingPostId(postId);
    await supabase
      .from('openmarket_posts')
      .delete()
      .eq('id', postId)
      .eq('user_id', currentUser?.uid);
    await loadFeed(true);
    setDeletingPostId(null);
  };

  // ============================================================
  // Shop navigation
  // ============================================================
  const handleShopClick = (shops: VendorShop[]) => {
    if (!shops.length) return;
    navigate(`/vendor/${shops[0].vendor_id}`);
  };

  // ============================================================
  // Fullscreen media
  // ============================================================
  const openFullscreen = (urls: string[], types: string[], index: number) => {
    setSelectedPostMedia({ urls, types, index });
  };

  // ============================================================
  // Video tap toggle
  // ============================================================
  const toggleVideo = (postId: string) => {
    const el = videoRefs.current[postId];
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => { /* noop */ });
      setVideoPaused(prev => ({ ...prev, [postId]: false }));
    } else {
      el.pause();
      setVideoPaused(prev => ({ ...prev, [postId]: true }));
    }
  };

  const toggleAudio = (postId: string) => {
    const el = videoRefs.current[postId] as unknown as HTMLAudioElement | null;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => { /* noop */ });
      setVideoPaused(prev => ({ ...prev, [postId]: false }));
    } else {
      el.pause();
      setVideoPaused(prev => ({ ...prev, [postId]: true }));
    }
  };

  // ============================================================
  // Search
  // ============================================================
  const loadSearchHistory = useCallback(async () => {
    if (!currentUser) return;
    try {
      const { data } = await supabase
        .from('search_history')
        .select('search_term, frequency, updated_at')
        .eq('user_id', currentUser.uid)
        .order('updated_at', { ascending: false })
        .limit(15);
      setSearchHistory((data || []).map((d: any) => d.search_term).filter(Boolean));
    } catch { setSearchHistory([]); }
  }, [currentUser]);

  const saveSearchHistory = useCallback(async (term: string) => {
    if (!currentUser) return;
    const clean = term.trim().toLowerCase();
    if (clean.length < 2) return;
    try {
      const { data: existing } = await supabase
        .from('search_history')
        .select('id, frequency')
        .eq('user_id', currentUser.uid)
        .eq('search_term', clean)
        .maybeSingle();
      const now = new Date().toISOString();
      if (existing) {
        await supabase
          .from('search_history')
          .update({ frequency: (existing.frequency || 1) + 1, updated_at: now })
          .eq('id', existing.id);
      } else {
        await supabase.from('search_history').insert({
          user_id: currentUser.uid,
          search_term: clean,
          frequency: 1,
          created_at: now,
          updated_at: now
        });
      }
      loadSearchHistory();
    } catch { /* silent */ }
  }, [currentUser, loadSearchHistory]);

  const performSearch = useCallback(async (term: string) => {
    const q = term.trim();
    if (!q || q.length < 2) return;
    setSearching(true);
    setSearchMode(true);
    saveSearchHistory(q);

    try {
      const nowIso = new Date().toISOString();
      const pattern = `%${q.toLowerCase()}%`;

      const { data } = await supabase
        .from('openmarket_posts')
        .select('*')
        .gt('expires_at', nowIso)
        .ilike('text', pattern)
        .order('created_at', { ascending: false })
        .limit(60);

      const enriched = await enrichPosts(data || []);
      setSearchResults(enriched);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, [enrichPosts, saveSearchHistory]);

  const handleSearchSubmit = () => {
    if (searchInput.trim().length < 2) return;
    performSearch(searchInput);
  };

  const handleSearchHistoryClick = (term: string) => {
    setSearchInput(term);
    performSearch(term);
  };

  const clearSearchMode = () => {
    setSearchMode(false);
    setSearchResults([]);
    setSearchInput('');
  };

  useEffect(() => {
    if (searchOpen) loadSearchHistory();
  }, [searchOpen, loadSearchHistory]);

  // ============================================================
  // Bottom nav
  // ============================================================
  const handleNavHome = () => navigate('/user/dashboard');
  const handleNavLike = () => { setNavFilter(prev => prev === 'liked' ? 'none' : 'liked'); setTab('new'); };
  const handleNavMe = () => { setNavFilter(prev => prev === 'me' ? 'none' : 'me'); setTab('new'); };
  const handleNavShop = () => navigate('/vendor/dashboard');
  const handleBottomPlus = () => {
    if (tab === 'live') handleGoLive();
    else handleOpenComposer();
  };

  // ============================================================
  // Tabs
  // ============================================================
  const tabs: { key: TabKey; label: string }[] = [
    { key: 'new', label: 'New' },
    { key: 'trending', label: 'Trending Now' },
    { key: 'foryou', label: 'For You' },
    { key: 'following', label: 'Following' },
    { key: 'live', label: 'Live Markets' },
    { key: 'insight', label: 'Vendor Search Insight' }
  ];

  // ============================================================
  // Loading gate
  // ============================================================
  if (loading && !posts.length && !currentUser) {
    return (
      <div className="social-loading">
        <div className="social-spinner" />
      </div>
    );
  }

  const displayedPosts = searchMode ? searchResults : posts;

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="social-container">

      {/* ============ TOP BAR ============ */}
      <header className="social-header">
        <button className="social-back" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <h1 className="social-title">
          <Users size={18} /> Social
        </h1>
        <button
          className={`social-applogo ${searchOpen ? 'active' : ''}`}
          onClick={() => {
            if (searchOpen) {
              setSearchOpen(false);
              clearSearchMode();
            } else {
              setSearchOpen(true);
            }
          }}
          aria-label={searchOpen ? 'Cancel search' : 'Search'}
        >
          {searchOpen ? <X size={18} /> : <Search size={18} />}
        </button>
      </header>

      {/* ============ SEARCH BAR ============ */}
      {searchOpen && (
        <div className="social-searchbar">
          <input
            type="text"
            className="social-search-input"
            placeholder="Search posts, hashtags..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearchSubmit(); }}
            autoFocus
          />
          <button
            className="social-search-go"
            onClick={handleSearchSubmit}
            disabled={searchInput.trim().length < 2 || searching}
          >
            {searching ? '...' : 'Go'}
          </button>
          {searchMode && (
            <button className="social-search-clear" onClick={clearSearchMode}>Clear</button>
          )}
        </div>
      )}

      {/* Search history chips */}
      {searchOpen && !searchMode && searchHistory.length > 0 && (
        <div className="social-search-history">
          {searchHistory.map((term, i) => (
            <button
              key={`${term}_${i}`}
              className="social-search-history-chip"
              onClick={() => handleSearchHistoryClick(term)}
            >
              {term}
            </button>
          ))}
        </div>
      )}

      {/* ============ TABS ============ */}
      {!searchMode && (
        <div className="social-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              className={`social-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* ============ INSIGHT TAB ============ */}
      {!searchMode && tab === 'insight' ? (
        <div className="social-insight">
          <Search size={40} />
          <h3>Vendor Search Insights</h3>
          <p>Be the first to see what customers are actively searching for: untapped business niches, uncovered location demand, supply chain & logistics bottlenecks... — coming soon.</p>
        </div>
      ) : (
        <>
          {/* ============ FEED ============ */}
          <div
            ref={feedRef}
            className="social-feed"
            onScroll={handleScrollProxy}
          >
            {displayedPosts.length === 0 && !loading ? (
              <div className="social-empty">
                <p>{searchMode ? 'No matching posts' : 'Loading...'}</p>
              </div>
            ) : (
              displayedPosts.map((post, idx) => {
                const isActive = idx === activeIndex && !searchMode;
                const hasMedia = !!(post.media_urls && post.media_urls.length);
                const firstMediaType = post.media_types?.[0];
                const firstMediaUrl = post.media_urls?.[0];
                const isOwner = currentUser?.uid === post.user_id;
                const parsed = parsePostText(post.text || '');
                const title = parsed.title;
                const description = parsed.description;
                const hashtags = parsed.hashtags;
                const linkedRoom = post.live_room_id
                  ? videoRooms.find(r => r.id === post.live_room_id)
                  : null;
                const isLiveStarter = !!post.live_room_id && post.user_id === currentUser?.uid;

                return (
                  <div
                    key={post.id}
                    className="social-reel"
                    data-active={isActive}
                  >
                    {/* Media stage */}
                    <div className="social-reel-stage">
                      {hasMedia ? (
                        firstMediaType === 'video' ? (
                          <video
                            ref={el => { videoRefs.current[post.id] = el; }}
                            src={firstMediaUrl}
                            className="social-reel-media"
                            loop
                            playsInline
                            muted={false}
                            onClick={() => toggleVideo(post.id)}
                          />
                        ) : firstMediaType === 'audio' ? (
                          <div
                            className="social-reel-audio"
                            onClick={() => toggleAudio(post.id)}
                            role="button"
                            tabIndex={0}
                          >
                            <Music size={64} />
                            <audio
                              ref={el => { videoRefs.current[post.id] = el as any; }}
                              src={firstMediaUrl}
                              loop
                              autoPlay={isActive}
                            />
                            <div className="social-reel-audio-label">Audio</div>
                          </div>
                        ) : (
                          <img
                            src={firstMediaUrl}
                            className="social-reel-media"
                            alt=""
                            onClick={() => openFullscreen(post.media_urls!, post.media_types!, 0)}
                          />
                        )
                      ) : (
                        <div className="social-reel-textonly">
                          {title && <h3 className="social-reel-title">{title}</h3>}
                          {description && <p className="social-reel-description">{description}</p>}
                          {hashtags.length > 0 && (
                            <div className="social-reel-hashtags">
                              {hashtags.map((t, i) => (
                                <span key={i} className="social-hashtag">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Bottom-left info */}
                    <div className="social-reel-info">
                      <div className="social-reel-user">
                        {post.user_avatar ? (
                          <img src={post.user_avatar} alt="" className="social-reel-avatar" />
                        ) : (
                          <div className="social-reel-avatar placeholder">
                            {post.user_name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <span className="social-reel-username">@{post.user_name}</span>
                        <span className="social-reel-time">· {formatTimeAgo(post.created_at)}</span>
                      </div>
                      {hasMedia && (title || description) && (
                        <div className="social-reel-caption">
                          {title && <strong>{title} </strong>}
                          {description}
                        </div>
                      )}
                      {hasMedia && hashtags.length > 0 && (
                        <div className="social-reel-hashtags">
                          {hashtags.map((t, i) => (
                            <span key={i} className="social-hashtag">{t}</span>
                          ))}
                        </div>
                      )}
{post.vendor_shops && post.vendor_shops.length > 0 && (
  <div className="social-reel-shops-row">
    {post.vendor_shops.map((shop, i) => (
      <React.Fragment key={shop.id}>
        <button
          className="social-reel-shop-link"
          onClick={() => navigate(`/vendor/${shop.vendor_id}`)}
        >
          <Store size={12} /> {shop.shop_name}
        </button>
        <VerifiedBadge
          shopId={shop.vendor_id}
          variant="badge"
          className="social-reel-shop-verified"
        />
        {i < post.vendor_shops!.length - 1 && (
          <span className="social-reel-shop-sep">·</span>
        )}
      </React.Fragment>
    ))}
  </div>
)}

                      {/* Live actions row */}
                      {post.live_room_id && (
                        <div className="social-reel-live-row">
                          <button
                            className="social-live-join-btn"
                            onClick={() => {
                              const room = linkedRoom || {
                                id: post.live_room_id!,
                                room_name: (post.text || 'Live').replace(/^🔴\s*Went live:\s*/i, '').trim() || 'Live',
                                created_by_user_id: post.user_id,
                                created_by_name: post.user_name || 'User',
                                created_by_avatar: post.user_avatar,
                                participant_count: 0,
                                is_active: true,
                                created_at: post.created_at,
                                last_activity: post.created_at,
                                live_link: null
                              };
                              handleJoinRoom(room as VideoRoom);
                            }}
                          >
                            <Play size={12} /> Join Live
                            {linkedRoom && linkedRoom.participant_count > 0 && (
                              <span style={{ marginLeft: 4, opacity: 0.85 }}>
                                · {linkedRoom.participant_count}
                              </span>
                            )}
                          </button>

                          {isLiveStarter && (
                            <button
                              className="social-live-paste-btn"
                              onClick={() => handlePasteLiveLink(post.live_room_id!)}
                            >
                              <LinkIcon size={12} />
                              {linkedRoom?.live_link ? 'Pasted — paste again' : 'Paste live link'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right rail */}
                    <div className="social-reel-rail">
                      <button
                        className="social-rail-btn"
                        onClick={() => handleOpenViews(post.id)}
                        aria-label="Views"
                      >
                        <Eye size={26} />
                        <span>{post.views_count || 0}</span>
                      </button>

                      <button
                        className={`social-rail-btn ${post.is_liked_by_user ? 'liked' : ''}`}
                        onClick={() => handleLikePost(post.id)}
                        aria-label="Like"
                      >
                        <Heart size={26} fill={post.is_liked_by_user ? 'currentColor' : 'none'} />
                        <span>{post.likes_count || 0}</span>
                      </button>

                      <button
                        className="social-rail-btn"
                        onClick={() => handleOpenComments(post.id)}
                        aria-label="Comments"
                      >
                        <MessageCircle size={26} />
                        <span>{post.comments_count || 0}</span>
                      </button>

                      <button
                        className="social-rail-btn"
                        onClick={() => handleSharePost(post)}
                        aria-label="Share"
                      >
                        <Share2 size={26} />
                        <span>Share</span>
                      </button>

                      <button
                        className="social-rail-btn"
                        onClick={() => handleSavePost(post)}
                        aria-label="Save"
                      >
                        <Bookmark size={26} />
                        <span>Save</span>
                      </button>

                      {isOwner && (
                        <div className="social-rail-owner">
                          <button
                            className="social-rail-btn small"
                            onClick={() => handleStartEdit(post)}
                            aria-label="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            className="social-rail-btn small"
                            onClick={() => handleDeletePost(post.id)}
                            disabled={deletingPostId === post.id}
                            aria-label="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Edit overlay */}
                    {editingPostId === post.id && (
                      <div className="social-reel-edit-overlay">
                        <input
                          className="social-edit-input"
                          placeholder="Title"
                          value={editingTitle}
                          onChange={e => setEditingTitle(e.target.value)}
                        />
                        <textarea
                          className="social-edit-textarea"
                          placeholder="Description"
                          value={editingDescription}
                          onChange={e => setEditingDescription(e.target.value)}
                          rows={3}
                        />
                        <div className="social-edit-hashtags">
                          {editingHashtags.map((tag, i) => (
                            <div key={i} className="social-edit-hashtag-input">
                              <span className="social-hash-prefix">#</span>
                              <input
                                value={tag}
                                placeholder={`hashtag ${i + 1}`}
                                onChange={e => handleEditHashtagChange(i, e.target.value)}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="social-edit-actions">
                          <button onClick={() => handleEditPost(post.id)}>Save</button>
                          <button onClick={() => {
                            setEditingPostId(null);
                            setEditingTitle('');
                            setEditingDescription('');
                            setEditingHashtags(['', '', '', '', '']);
                          }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {loadingMore && (
              <div className="social-loading-more">
                <div className="social-spinner small" />
              </div>
            )}
          </div>

          {/* ============ BOTTOM NAV ============ */}
          {!searchMode && (
            <nav className="social-bottomnav">
              <button className="social-bn-btn" onClick={handleNavHome} aria-label="Home">
                <Home size={20} />
              </button>
              <button
                className={`social-bn-btn ${navFilter === 'liked' ? 'active' : ''}`}
                onClick={handleNavLike}
                aria-label="Liked"
              >
                <Heart size={20} fill={navFilter === 'liked' ? 'currentColor' : 'none'} />
              </button>
              <button className="social-bn-plus" onClick={handleBottomPlus} aria-label="Create">
                <Plus size={22} />
              </button>
              <button
                className={`social-bn-btn ${navFilter === 'me' ? 'active' : ''}`}
                onClick={handleNavMe}
                aria-label="Me"
              >
                <UserIcon size={20} />
              </button>
              <button className="social-bn-btn" onClick={handleNavShop} aria-label="Shop">
                <Store size={20} />
              </button>
            </nav>
          )}
        </>
      )}

      {/* ============ COMPOSER MODAL — multi-step full screen ============ */}
      {showComposer && (
        <div className="social-composer-overlay">
          <div className="social-composer-full">
            <div className="social-composer-header">
              <button className="social-composer-back" onClick={() => {
                if (composerStep === 1) handleCloseComposer();
                else setComposerStep((prev) => (prev - 1) as any);
              }}>
                <ArrowLeft size={20} />
              </button>
              <span className="social-composer-step">
                Step {composerStep} of 4
              </span>
              <button className="social-composer-close" onClick={handleCloseComposer}>
                <X size={20} />
              </button>
            </div>

            <div className="social-composer-body">
              {composerStep === 1 && (
                <div className="social-step">
                  <h2 className="social-step-title">Select Media</h2>
                  <p className="social-step-sub">Photos, videos, or voice notes</p>

                  <div className="social-media-picker-grid">
                    <label className="social-media-picker-card">
                      <ImageIcon size={28} />
                      <span>Gallery</span>
                      <input
                        type="file"
                        accept="image/*,video/*,audio/*"
                        multiple
                        onChange={e => { handleMediaSelect(e.target.files); e.target.value = ''; }}
                        style={{ display: 'none' }}
                      />
                    </label>

                    <button
                      className={`social-media-picker-card ${recordingAudio ? 'recording' : ''}`}
                      onClick={recordingAudio ? stopRecording : startRecording}
                    >
                      <Mic size={28} />
                      <span>{recordingAudio ? 'Stop' : 'Voice Note'}</span>
                    </button>
                  </div>

                  {newPostMedia.length > 0 && (
                    <div className="social-media-preview-grid">
                      {newPostMedia.map((m, i) => (
                        <div key={i} className="social-media-preview-item">
                          {m.type === 'image' && <img src={m.preview} alt="" />}
                          {m.type === 'video' && <video src={m.preview} />}
                          {m.type === 'audio' && <div className="social-audio-tile"><Music size={22} /></div>}
                          <button onClick={() => handleRemoveMedia(i)}>
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {composerStep === 2 && (
                <div className="social-step">
                  <h2 className="social-step-title">Add Title</h2>
                  <p className="social-step-sub">Optional — a short caption</p>
                  <input
                    className="social-input-large"
                    type="text"
                    placeholder="Title"
                    value={newPostTitle}
                    onChange={e => setNewPostTitle(e.target.value)}
                    maxLength={120}
                    autoFocus
                  />
                </div>
              )}

              {composerStep === 3 && (
                <div className="social-step">
                  <h2 className="social-step-title">Add Description</h2>
                  <p className="social-step-sub">Optional — tell your story</p>
                  <textarea
                    className="social-textarea-large"
                    placeholder="What's this about?"
                    value={newPostDescription}
                    onChange={e => setNewPostDescription(e.target.value)}
                    rows={6}
                    maxLength={1000}
                    autoFocus
                  />
                </div>
              )}

              {composerStep === 4 && (
                <div className="social-step">
                  <h2 className="social-step-title">Add Hashtags</h2>
                  <p className="social-step-sub">Up to 5 — we add the # for you</p>
                  <div className="social-hashtag-inputs">
                    {newPostHashtags.map((tag, i) => (
                      <div key={i} className="social-hashtag-input-wrap">
                        <span className="social-hash-prefix">#</span>
                        <input
                          value={tag}
                          placeholder={`hashtag ${i + 1}`}
                          onChange={e => handleHashtagChange(i, e.target.value)}
                          maxLength={30}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="social-composer-footer">
              {composerStep < 4 ? (
                <button
                  className="social-composer-next"
                  onClick={() => setComposerStep((prev) => (prev + 1) as any)}
                >
                  Next
                </button>
              ) : (
                <button
                  className="social-composer-submit"
                  onClick={handleSubmitPost}
                  disabled={uploadingPost}
                >
                  {uploadingPost ? 'Posting...' : 'Share Post'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ COMMENTS MODAL ============ */}
      {commentsOpenFor && (() => {
        const post = posts.find(p => p.id === commentsOpenFor) || searchResults.find(p => p.id === commentsOpenFor);
        if (!post) return null;
        return (
          <div className="social-comments-overlay" onClick={() => setCommentsOpenFor(null)}>
            <div className="social-comments-sheet" onClick={e => e.stopPropagation()}>
              <div className="social-comments-header">
                <h3>{post.comments_count || 0} comments</h3>
                <button onClick={() => setCommentsOpenFor(null)}>
                  <X size={20} />
                </button>
              </div>
              <div className="social-comments-scroll">
                {(post.comments || []).map(c => (
                  <div key={c.id} className="social-comment-item">
                    <div className="social-comment-user">
                      {c.user_avatar
                        ? <img src={c.user_avatar} alt="" />
                        : <span>{c.user_name?.charAt(0) || 'U'}</span>}
                      <strong>{c.user_name}</strong>
                    </div>
                    <div className="social-comment-text">{c.text}</div>
                    <div className="social-comment-time">{formatTimeAgo(c.created_at)}</div>
                  </div>
                ))}
                {(!post.comments || post.comments.length === 0) && (
                  <div className="social-comments-empty">No comments yet</div>
                )}
              </div>
              <div className="social-comments-input-row">
                <input
                  type="text"
                  placeholder="Add a comment..."
                  value={commentInput}
                  onChange={e => setCommentInput(e.target.value)}
                />
                <button
                  onClick={() => handleAddComment(post.id)}
                  disabled={submittingComment || !commentInput.trim()}
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ============ VIEWERS MODAL ============ */}
      {viewsOpenFor && (() => {
        const post = posts.find(p => p.id === viewsOpenFor) || searchResults.find(p => p.id === viewsOpenFor);
        if (!post) return null;
        return (
          <div className="social-views-overlay" onClick={() => setViewsOpenFor(null)}>
            <div className="social-views-sheet" onClick={e => e.stopPropagation()}>
              <div className="social-views-header">
                <h3>Views · {post.views_count || 0}</h3>
                <button onClick={() => setViewsOpenFor(null)}>
                  <X size={20} />
                </button>
              </div>
              <div className="social-views-scroll">
                {loadingViewers ? (
                  <div className="social-views-loading">Loading...</div>
                ) : viewers.length === 0 ? (
                  <div className="social-views-empty">No views yet</div>
                ) : (
                  viewers.map(v => (
                    <div key={v.id} className="social-viewer-row">
                      {v.viewer_avatar ? (
                        <img src={v.viewer_avatar} alt="" className="social-viewer-avatar" />
                      ) : (
                        <div className="social-viewer-avatar placeholder">
                          {v.viewer_name?.charAt(0) || 'V'}
                        </div>
                      )}
                      <div className="social-viewer-info">
                        <span className="social-viewer-name">{v.viewer_name}</span>
                        <span className="social-viewer-type">{v.viewer_type}</span>
                      </div>
                      <span className="social-viewer-count">{v.views}×</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ============ FULLSCREEN MEDIA ============ */}
      {selectedPostMedia && (
        <div className="social-fullscreen" onClick={() => setSelectedPostMedia(null)}>
          <button className="social-fullscreen-close" onClick={() => setSelectedPostMedia(null)}>
            <X size={24} />
          </button>
          {selectedPostMedia.urls.length > 1 && (
            <>
              <button
                className="social-fullscreen-prev"
                onClick={e => {
                  e.stopPropagation();
                  const i = Math.max(0, selectedPostMedia.index - 1);
                  setSelectedPostMedia({ ...selectedPostMedia, index: i });
                }}
              >
                <ChevronLeft size={28} />
              </button>
              <button
                className="social-fullscreen-next"
                onClick={e => {
                  e.stopPropagation();
                  const i = Math.min(selectedPostMedia.urls.length - 1, selectedPostMedia.index + 1);
                  setSelectedPostMedia({ ...selectedPostMedia, index: i });
                }}
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}
          {selectedPostMedia.types[selectedPostMedia.index] === 'video' ? (
            <video
              src={selectedPostMedia.urls[selectedPostMedia.index]}
              controls
              autoPlay
              onClick={e => e.stopPropagation()}
            />
          ) : selectedPostMedia.types[selectedPostMedia.index] === 'audio' ? (
            <audio
              src={selectedPostMedia.urls[selectedPostMedia.index]}
              controls
              autoPlay
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={selectedPostMedia.urls[selectedPostMedia.index]}
              alt=""
              onClick={e => e.stopPropagation()}
            />
          )}
        </div>
      )}

      {/* ============ LIVE: single floating panel with iframe ============
          Same core behaviour as old version: the iframe src is derived
          from the immutable room_name, so both host and joiner join the
          exact same Mirotalk room. No link sharing required.
          If the host pasted an external live_link, that URL is used
          instead (fallback only — harmless). */}
      {activeLiveRoom && (() => {
        const liveSrc = activeLiveRoom.live_link
          ? activeLiveRoom.live_link
          : `${MIROTALK_BASE}${encodeURIComponent(activeLiveRoom.room_name)}`;
        const isOwner = currentUser?.uid === activeLiveRoom.created_by_user_id;
        return (
          <div
            ref={liveVideoRef}
            className={`social-floating-video ${liveVideoMinimized ? 'minimized' : ''}`}
            style={{
              left: liveVideoPosition.x,
              top: liveVideoPosition.y,
              width: liveVideoSize.width,
              height: liveVideoMinimized ? 50 : liveVideoSize.height
            }}
          >
            <div className="social-floating-video-header" onMouseDown={handleDragStart}>
              <div className="social-drag-handle"><Move size={14} /></div>
              <span>
                {activeLiveRoom.room_name}
                {typeof activeLiveRoom.participant_count === 'number' && (
                  <span style={{ marginLeft: 6, opacity: 0.8, fontWeight: 600 }}>
                    · {activeLiveRoom.participant_count} <Users size={11} style={{ verticalAlign: '-1px' }} />
                  </span>
                )}
              </span>
              <div className="social-video-controls">
                <button onClick={() => setLiveVideoMinimized(!liveVideoMinimized)}>
                  {liveVideoMinimized ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
                </button>
                <button onClick={() => {
                  setLiveVideoSize({
                    width: Math.min(window.innerWidth - 40, liveVideoSize.width + 50),
                    height: Math.min(window.innerHeight - 100, liveVideoSize.height + 50)
                  });
                }}><Expand size={14} /></button>
                <button onClick={() => {
                  setLiveVideoSize({
                    width: Math.max(240, liveVideoSize.width - 50),
                    height: Math.max(180, liveVideoSize.height - 50)
                  });
                }}><Shrink size={14} /></button>
                {isOwner ? (
                  <button onClick={handleEndLive} title="End live"><PhoneOff size={14} /></button>
                ) : (
                  <button onClick={handleLeaveRoom} title="Leave live"><X size={14} /></button>
                )}
              </div>
            </div>
            {!liveVideoMinimized && (
              <>
                <iframe
                  src={liveSrc}
                  allow="camera; microphone; display-capture; fullscreen; autoplay; clipboard-write"
                  className="social-floating-video-iframe"
                  title="Live Video"
                />
                <div className="social-resize-handle" onMouseDown={handleResizeStart} />
              </>
            )}
          </div>
        );
      })()}

      {/* ============ TOAST ============ */}
      {toast && (
        <div className="social-toast">
          <Check size={16} />
          <span>{toast}</span>
        </div>
      )}
    </div>
  );

  // proxy for scroll (kept after render so it uses latest)
  function handleScrollProxy() {
    handleFeedScroll();
  }
};

export default OpenMarket;